import _ from 'lodash';
import {
  AdType,
  CreativeDeliverType,
  RtbCampaignPlanType,
  DeliverType,
} from 'core/rtbCampaign/RtbCampaign';
import { getDayOfWeekLabelByValue } from 'components/Dayparts/Dayparts';
import { RtbCampaignSetupFlowPageModel } from '../RtbCampaignSetupFlowPageModel';
import { Currency } from 'core';
import { toast } from 'react-toastify';
import {
  FireableUpdateEventListener,
  UpdateEventListener,
} from 'utils/UpdateEventListener';
import {
  RtbCampaignManager,
  DefaultRtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import { Order } from 'core/order/Order';
import { CampaignSetupTab } from './RtbCampaignSetupStepModel';
import i18n from 'i18next';
import {
  CreateKeywordCampaignSetupFlowStep,
  CreateRtbCampaignSetupFlowStep,
  EditKeywordCampaignSetupFlowStep,
  EditRtbCampaignSetupFlowStep,
} from '../RtbCampaignSetupFlowStep';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import { BidStrategy } from 'core/l2Object/L2Object';
import { CreativeSummaryStepModel } from 'containers/Creatives/CreativeSetupFlow/FlowSteps/CreativeSummaryStepModel';

export type RtbCampaignSummaryStepState = {
  readonly loading: boolean;
  readonly showPublishBindingFailed: boolean;
};

export interface RtbCampaignSummaryStepModel {
  readonly flowModel: RtbCampaignSetupFlowPageModel;
  readonly event: UpdateEventListener<RtbCampaignSummaryStepModel>;
  readonly state: RtbCampaignSummaryStepState;
  readonly targetingValue: any;
  readonly order: Order;
  readonly showLegacyEstimation: boolean;
  readonly subStepIndex: number;
  readonly creativeSummaryModel?: CreativeSummaryStepModel;
  updateState(loading: boolean): void;
  getAdTypeSummaryData: () => any;
  getProductSummaryData: () => any;
  getSetUpSummaryData: () => any;
  getSetUpMaxCvrKeywordSummaryData: () => any;
  getLimitationsSummaryData: () => any;
  submit: (afterSubmit?: (campaignId: number) => void) => void;
  goLast: () => void;
  goStep: (stepIndex: number, subStepIndex: number) => void;
  goSubStep: (subStepIndex: number) => void;
  redirectToLastPage: () => void;
}

export type RtbCampaignSummaryStepProps = {
  readonly model: RtbCampaignSummaryStepModel;
};

export class DefaultRtbCampaignSummaryStepModel
  implements RtbCampaignSummaryStepModel
{
  event: FireableUpdateEventListener<RtbCampaignSummaryStepModel>;
  loading: boolean;
  showPublishBindingFailed: boolean = false;
  stepChangeListener?: (stepIndex, subStepName?: string) => void;

  constructor(
    public flowModel: RtbCampaignSetupFlowPageModel,
    public goLast: () => void,
    public goStep: (stepIndex: number, subStepIndex: number) => void,
    public goSubStep: (subStepIndex: number) => void,
    public subStepIndex: number,
    public creativeSummaryModel?: CreativeSummaryStepModel,
    protected campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager(),
  ) {
    this.event = new FireableUpdateEventListener<RtbCampaignSummaryStepModel>();
    this.loading = false;
  }

  get state() {
    return {
      loading: this.loading,
      showPublishBindingFailed: this.showPublishBindingFailed,
    };
  }

  get showLegacyEstimation() {
    return true;
  }

  get targetingValue() {
    return this.flowModel.limitationModel
      ? this.flowModel.limitationModel.limitationValue
      : [];
  }

  get order() {
    return this.flowModel.order;
  }

  getAdTypeSummaryData() {
    return {
      title: i18n.t<string>('campaignSummary.titles.adType'),
      backStep: CreateRtbCampaignSetupFlowStep.CHOOSE_ADTYPE,
      data: {
        adType: {
          title: undefined,
          content: [
            {
              label: i18n.t<string>('campaign.labels.adType'),
              value: this.getAdTypeValue(
                this.flowModel.state.campaign.basic.adType,
              ),
            },
          ],
        },
      },
    };
  }
  getProductSummaryData() {
    // 根據選品方式呈現對應的商品
    const productType = (pattern) => {
      if (pattern === 'AUTO') {
        return this.flowModel.state.campaign.autoProducts;
      }
      return this.flowModel.state.campaign.products;
    };
    const productNames = productType(
      this.flowModel.state.campaign.basic.selectPattern,
    )
      .map((product) => product.name)
      .join(', ');
    return {
      title: i18n.t<string>('campaignSummary.titles.productSettimg'),
      backStep: CreateRtbCampaignSetupFlowStep.CHOOSE_PRODUCTS,
      data: {
        adType: {
          title: undefined,
          content: [
            {
              label: i18n.t<string>('campaign.labels.productSelectionType'),
              value: this.getSelectPatternValue(
                this.flowModel.state.campaign.basic.selectPattern,
              ),
            },
            {
              label: i18n.t<string>('campaign.labels.productSelection'),
              value: productNames,
            },
          ],
        },
      },
    };
  }
  get campaignSetupStepIndex() {
    const isCreate = this.flowModel.type === 'create';
    const isKeyword = [
      AdType.KEYWORD,
      AdType.SMART_CONFIG,
      AdType.SMART_KEYWORD,
      AdType.MAX_CVR_KEYWORD,
    ].includes(this.flowModel.state.campaign.basic.adType);
    if (isKeyword) {
      return isCreate
        ? CreateKeywordCampaignSetupFlowStep.SETUP_CAMPAIGN
        : EditKeywordCampaignSetupFlowStep.SETUP_CAMPAIGN;
    }
    return isCreate
      ? CreateRtbCampaignSetupFlowStep.SETUP_CAMPAIGN
      : EditRtbCampaignSetupFlowStep.SETUP_CAMPAIGN;
  }

  getSetUpSummaryData() {
    const campaignSetUpData = this.flowModel.state.campaign.basic;
    const adType = campaignSetUpData.adType;
    const campaignBasicModel = this.flowModel.getRtbCampaignBasicFormModel(
      campaignSetUpData.adType,
    );
    const priceModelRelatedSummaryData = this.getPriceModelRelatedSummaryData(
      campaignSetUpData.priceModel,
    );
    const showDeliverType = _.get(campaignBasicModel, 'showDeliverType', true);
    const showOptimizeSection = _.get(
      campaignBasicModel,
      'showOptimizeSection',
      true,
    );
    const showBidPriceInGeneral =
      campaignSetUpData.priceModel === RtbCampaignPlanType.RS_CPC ||
      campaignSetUpData.priceModel === RtbCampaignPlanType.RS_CPM;
    return {
      title: i18n.t<string>('campaignSummary.titles.basicSetting'),
      backStep: this.campaignSetupStepIndex,
      backSubStep: CampaignSetupTab.BASIC,
      data: _.omitBy(
        {
          general: {
            title:
              adType === 'MAX_CVR_KEYWORD'
                ? ''
                : i18n.t<string>('campaignSummary.titles.generalInformation'),
            content: _.compact(
              _.concat(
                [
                  {
                    label: i18n.t<string>('campaignSummary.labels.orderName'),
                    value: this.flowModel.order.projectName,
                  },
                  {
                    label: i18n.t<string>(
                      'campaignSummary.labels.campaignName',
                    ),
                    value: campaignSetUpData.name,
                  },
                  adType === 'SHOP'
                    ? {
                        label: i18n.t<string>('campaignSummary.labels.brand'),
                        value: campaignSetUpData.brandLabel,
                      }
                    : {},
                  {
                    label: i18n.t<string>('campaignSummary.labels.schedule'),
                    value: `${campaignSetUpData.startDate} ~ ${campaignSetUpData.endDate}`,
                  },
                  {
                    label: i18n.t<string>(
                      'campaignSummary.labels.pricingModel',
                    ),
                    value: this.getCampaignPlanTypeValue(
                      campaignSetUpData.priceModel,
                    ),
                  },
                  showBidPriceInGeneral &&
                  campaignSetUpData.bidStrategy ===
                    BidStrategy.LOWEST_COST_WITH_BID_CAP
                    ? {
                        label: i18n.t<string>('campaignForm.labels.bidControl'),
                        value: formatPriceWithCurrency(
                          this.flowModel.order.currency,
                          +campaignSetUpData.bidPrice,
                        ),
                      }
                    : undefined,
                  showDeliverType
                    ? {
                        label: i18n.t('campaignSummary.labels.deliverType'),
                        value: this.getDeliverTypeValue(
                          campaignSetUpData.deliverType,
                        ),
                      }
                    : undefined,
                ],
                priceModelRelatedSummaryData,
              ),
            ),
          },
          optimizeSetting: showOptimizeSection
            ? {
                title: i18n.t<string>('campaignForm.titles.optimization'),
                content: _.compact([
                  {
                    label: i18n.t<string>(
                      'campaignForm.labels.optimizationGoal',
                    ),
                    value: i18n.t<string>(
                      `optimizationGoal.${campaignSetUpData.optimize.toLowerCase()}`,
                    ),
                  },
                  !showBidPriceInGeneral &&
                  campaignSetUpData.bidStrategy ===
                    BidStrategy.LOWEST_COST_WITH_BID_CAP
                    ? {
                        label: i18n.t<string>('campaignForm.labels.bidControl'),
                        value: formatPriceWithCurrency(
                          this.flowModel.order.currency,
                          +campaignSetUpData.bidPrice,
                        ),
                      }
                    : undefined,
                  {
                    label: i18n.t<string>('campaignForm.labels.billingEvent'),
                    value:
                      campaignSetUpData.priceModel === RtbCampaignPlanType.RS
                        ? i18n.t<string>(
                            `rtbCampaign.billingEvent.${campaignSetUpData.priceModel}.${campaignSetUpData.optimize.toLowerCase()}`,
                          )
                        : i18n.t<string>(
                            `rtbCampaign.billingEvent.${campaignSetUpData.priceModel}`,
                          ),
                  },
                  campaignSetUpData.frequency &&
                  !_.isEmpty(campaignSetUpData.frequency)
                    ? {
                        label: i18n.t<string>(
                          'campaignForm.labels.frequencyControl',
                        ),
                        value: this.getSessionCappaingCountValue(
                          +campaignSetUpData.frequency.maxFrequency,
                          +campaignSetUpData.frequency.intervalDays,
                        ),
                      }
                    : undefined,
                  campaignSetUpData.dayPart && {
                    label: i18n.t<string>('campaignSummary.labels.dayPart'),
                    value: this.getDayPartValue(campaignSetUpData.dayPart),
                  },
                ]),
              }
            : undefined,
        },
        _.isUndefined,
      ),
    };
  }

  getSetUpMaxCvrKeywordSummaryData() {
    const campaignSetUpData = this.flowModel.state.campaign.basic;
    const campaignBasicModel = this.flowModel.getRtbCampaignBasicFormModel(
      campaignSetUpData.adType,
    );
    const budgetType = campaignSetUpData.budgetType || null;
    const needShowDailyTargetBudget = budgetType === 'DAILY';
    const runningPattern = campaignSetUpData.runningPattern;
    const showDeliverType = _.get(campaignBasicModel, 'showDeliverType', true);
    const currency = this.flowModel
      ? this.flowModel.order.currency
      : Currency.NTD;
    return {
      title: i18n.t<string>('campaignSummary.titles.basicSetting'),
      backStep: this.campaignSetupStepIndex,
      backSubStep: CampaignSetupTab.BASIC,
      data: _.omitBy(
        {
          general: {
            title: '',
            content: _.compact(
              _.concat([
                {
                  label: i18n.t<string>('campaignSummary.labels.orderName'),
                  value: this.flowModel.order.projectName,
                },
                {
                  label: i18n.t<string>('campaignSummary.labels.campaignName'),
                  value: campaignSetUpData.name,
                },
                {
                  label: i18n.t<string>('campaignSummary.labels.brand'),
                  value: campaignSetUpData.brandLabel,
                },
                {
                  label: i18n.t<string>('campaignSummary.labels.schedule'),
                  value: this.getRunningPatternValue(campaignSetUpData),
                },
                {
                  label: i18n.t<string>('campaignSummary.labels.roasModel'),
                  value: this.getRoasValue(campaignSetUpData.roasStrategy),
                },
                showDeliverType
                  ? {
                      label: i18n.t('campaignSummary.labels.deliverType'),
                      value: this.getDeliverTypeValue(
                        campaignSetUpData.deliverType,
                      ),
                    }
                  : undefined,
                needShowDailyTargetBudget && {
                  label: i18n.t<string>(
                    'campaignSummary.labels.budgetDistribution',
                  ),
                  value: this.getOptimizeBudgetValue(
                    campaignSetUpData.dailyTargetBudget,
                  ),
                },
                runningPattern === 'LIMITED' &&
                  budgetType === 'TOTAL' && {
                    label: i18n.t<string>(
                      'campaignSummary.labels.campaignBudget',
                    ),
                    value: `${currency} ${campaignSetUpData.budget}`,
                  },
              ]),
            ),
          },
        },
        _.isUndefined,
      ),
    };
  }

  getLimitationsSummaryData(): any {
    const limitations = this.flowModel.state.campaign.limitations;
    const summaryData = this.flowModel.getLimitationsSummaryData(limitations);
    return {
      title: i18n.t<string>('campaignSummary.titles.targetSetting'),
      backStep: this.campaignSetupStepIndex,
      backSubStep: CampaignSetupTab.LIMITATION,
      data: summaryData,
    };
  }

  getPriceModelRelatedSummaryData(priceModel) {
    const currency = this.flowModel.order.currency;
    const campaignSetUpData = this.flowModel.state.campaign.basic;
    const needShowOrderPrice = priceModel !== RtbCampaignPlanType.RS;
    const needShowDailyTargetBudget = true;
    return _.compact([
      needShowOrderPrice &&
        campaignSetUpData.orderPrice && {
          label: i18n.t<string>(
            'l1Object.labels.bidStrategy.lowest_cost_with_bid_cap',
          ),
          // i18n.t<string>('campaignSummary.labels.orderPrice', { model: this.getCampaignPlanTypeValue(priceModel) }),
          value: `${currency} ${campaignSetUpData.orderPrice}`,
        },
      {
        label: i18n.t<string>('campaignSummary.labels.campaignBudget'),
        value: `${currency} ${campaignSetUpData.budget}`,
      },
      needShowDailyTargetBudget && {
        label: i18n.t<string>('campaignSummary.labels.budgetDistribution'),
        value: this.getOptimizeBudgetValue(campaignSetUpData.dailyTargetBudget),
      },
    ]);
  }

  getOptimizeBudgetValue(dailyTargetBudget) {
    const currency = this.flowModel
      ? this.flowModel.order.currency
      : Currency.NTD;
    if (
      dailyTargetBudget === null ||
      dailyTargetBudget === undefined ||
      dailyTargetBudget.toString() === '0'
    ) {
      return i18n.t<string>(
        'campaignSummary.labels.budgetScheduleDistribution',
      );
    } else {
      return i18n.t<string>('campaignSummary.labels.budgetDailyDistribution', {
        currency: currency,
        budget: dailyTargetBudget,
      });
    }
  }

  getDayPartValue(dayPart) {
    let dayPartValue = _.omitBy(_.omit(dayPart, 'enabled'), _.isEmpty);
    return Object.keys(dayPartValue)
      .map((day) => {
        return `${getDayOfWeekLabelByValue(parseInt(day, 10))},${i18n.t<string>('daypart.labels.hourUnit')}: ${dayPartValue[day].join(', ')}`;
      })
      .join('\r\n');
  }

  getSessionCappaingCountValue(maxFrequency: number, intervalDays: number) {
    return i18n.t<string>('campaignForm.descriptions.frequencyControl', {
      event: i18n.t<string>('campaignForm.descriptions.impression'),
      eventPlural: +maxFrequency > 1 ? 's' : '',
      interval_days: intervalDays,
      max_frequency: maxFrequency,
      unit: i18n.t<string>('common.units.day'),
      unitPlural: +intervalDays > 1 ? 's' : '',
    });
  }

  getCampaignPlanTypeValue(campaignPlanType) {
    return i18n.t<string>(`campaign.labels.${campaignPlanType}`);
  }

  getAdTypeValue(adType) {
    if (!(adType in AdType)) {
      return adType;
    }
    return i18n.t<string>(
      `campaignSummary.labels.adType${_.upperFirst(_.camelCase(adType))}`,
    );
  }

  getRunningPatternValue(campaignSetUpData: any) {
    switch (campaignSetUpData.runningPattern) {
      case 'LIMITED':
        return `${campaignSetUpData.startDate} ~ ${campaignSetUpData.endDate}`;
      case 'UNLIMITED':
        return i18n.t<string>('campaignSummary.labels.infinitely');
      default:
        return '';
    }
  }

  getRoasValue(roas: string) {
    switch (roas) {
      case 'HIGH_ROAS':
        return i18n.t<string>('campaignSummary.labels.highRoas');
      case 'MEDIUM_ROAS':
        return i18n.t<string>('campaignSummary.labels.mediumRoas');
      case 'LOW_ROAS':
        return i18n.t<string>('campaignSummary.labels.lowRoas');
      default:
        return '';
    }
  }

  getDeliverTypeValue(deliverType) {
    switch (deliverType) {
      case DeliverType.ACCELERATED:
        return i18n.t<string>('campaignSummary.labels.accelerated');
      default:
        return i18n.t<string>('campaignSummary.labels.standard');
    }
  }

  getCreativeDeliverTypeValue(creativeDeliverType) {
    switch (creativeDeliverType) {
      case CreativeDeliverType.OPTIMIZE:
        return i18n.t<string>(
          'campaignSummary.labels.creativeOptimizeDelivery',
        );
      default:
        return i18n.t<string>('campaignSummary.labels.creativeUniformDelivery');
    }
  }
  getSelectPatternValue(selectPattern) {
    if (selectPattern === null) {
      return;
    }
    return selectPattern === 'AUTO'
      ? i18n.t<string>('智能選品')
      : i18n.t<string>('手動選品');
  }
  submit = async (afterSubmit?: (campaignId: number) => void) => {
    // if (this.flowModel.type === 'edit') {
    //   await this.updateCampaign(
    //     this.flowModel.state.campaign,
    //     this.flowModel.order,
    //     this.flowModel.state.campaign.basic.groupId,
    //     afterSubmit,
    //   );
    // } else if (this.flowModel.type === 'split') {
    //   // await this.splitCampaign(this.flowModel.state.campaign, this.flowModel.order, this.flowModel.campaignId, this.flowModel.state.campaign.basic.groupId, afterSubmit);
    // } else {
    //   await this.createCampaign(
    //     this.flowModel.state.campaign,
    //     this.flowModel.order,
    //     this.flowModel.order.l1Objects[0].l1ObjectId,
    //     afterSubmit,
    //   );
    // }
  };

  splitCampaign = async (
    campaign: any,
    order: Order,
    campaignId,
    l1ObjectId?: number,
    afterSubmit?: (campaignId: number) => void,
  ) => {
    this.updateState(true);
    try {
      const newCampaignId = await this.campaignManager.splitCampaign(
        campaign,
        campaignId,
        l1ObjectId,
      );
      toast.success(i18n.t<string>('common.messages.succeeded'));
      if (afterSubmit) {
        afterSubmit(newCampaignId);
      } else {
        this.redirectToLastPage();
      }
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  createCampaign = async (
    campaign: any,
    order: Order,
    l1ObjectId?: number,
    afterSubmit?: (campaignId: number) => void,
  ) => {
    this.updateState(true);
    try {
      let newCampaignId;
      if (this.flowModel.state.campaign.basic.selectPattern === 'AUTO') {
        campaign.products = this.flowModel.state.campaign.autoProducts;
      }
      newCampaignId = await this.campaignManager.createCampaign(
        campaign,
        l1ObjectId,
      );
      if (afterSubmit) {
        afterSubmit(newCampaignId);
      } else {
        toast.success(i18n.t<string>('common.messages.succeeded'));
        this.redirectToLastPage();
      }
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  updateCampaign = async (
    campaign: any,
    order: Order,
    l1ObjectId: number,
    afterSubmit,
  ) => {
    this.updateState(true);
    try {
      if (this.flowModel.state.campaign.basic.selectPattern === 'AUTO') {
        campaign.products = this.flowModel.state.campaign.autoProducts;
      }
      const newCampaignId = await this.campaignManager.updateCampaign(
        campaign,
        l1ObjectId,
      );
      if (afterSubmit) {
        afterSubmit(newCampaignId);
      } else {
        toast.success(i18n.t<string>('common.messages.succeeded'));
        this.redirectToLastPage();
      }
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.updateState(false);
    }
  };

  redirectToLastPage = () => {
    const order = this.flowModel.order;
    const redirectPath = `/orders/${order.orderNumber}`;
    this.flowModel.setFinishedRedirectPath(redirectPath);
  };

  updateState(loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
